// BUILD-046 correction cycle 1bb88b3e (stage B1, item N3): the state and rules of the Handoff dialog, which had none of its own (they lived inside
// components/HandoffModal.tsx). Behavior is UNCHANGED - the same destinations, the same people filter, the same request (POST /work/{id}/handoff with the
// legacy HandoffRequest), the same default next action, the same permission checks by the server. What changes is how it is described:
//   - a COMPACT destination list: a name per destination, with no repeated description of familiar departments;
//   - a concise next action and an optional note;
//   - ONE short consequence line, a constant: "Handoff completes this item and creates another. It is not reassignment." It replaces the sentence that
//     named the task and the explanations under each destination.
// A Handoff completes the original item and creates another for the target; it is not a reassignment, and it is not a way round the rule that a
// source-governed task completes through its source (the server rejects it). Nothing here completes anything.
//
// Pure module. Type-only import of the legacy request. Erasable TypeScript only.

import type { HandoffRequest } from '../../api/client';
import { COMPLETES_THROUGH_SOURCE_TEXT } from './doneNoteModel';
// D1 display cleanup: the people of the list are First name + last initial (their full name is each option's title and the request still carries the stored name);
// where two would look alike the full name or an identifier is added beside the short one.
import { disambiguatePeople } from './displayNames';

/** The confirm button names the outcome: the item is completed AND another is created. */
export const HANDOFF_CONFIRM_LABEL = 'Complete and hand off';
export const HANDOFF_SENDING_LABEL = 'Handing off…';
/** Shown INSTEAD of the dialog's form for a source-governed task: it completes through its source, and a handoff would complete it. */
export const HANDOFF_GOVERNED_TEXT = `${COMPLETES_THROUGH_SOURCE_TEXT} A handoff would complete it, so it is not available here.`;

export type HandoffOffer = { kind: 'form' } | { kind: 'governed'; text: string };

/**
 * What the dialog offers for this task. A source-governed task keeps its restriction (it completes ONLY through its source; the server rejects a handoff
 * of it): the dialog explains instead of showing a working form. Nothing else changes: the server still decides every other case.
 */
export function handoffOffer(item: { sourceGoverned?: unknown } | null | undefined): HandoffOffer {
  return item !== null && item !== undefined && item.sourceGoverned === true ? { kind: 'governed', text: HANDOFF_GOVERNED_TEXT } : { kind: 'form' };
}

/** The ONE consequence line of the dialog. */
export const HANDOFF_CONSEQUENCE_LINE = 'Handoff completes this item and creates another. It is not reassignment.';
/** What the recipient sees as the next action when none is typed (unchanged). */
export const HANDOFF_DEFAULT_NEXT_ACTION = 'Review and accept handoff';
export const HANDOFF_FAILED_TEXT = 'Handoff failed';

export interface HandoffDestination {
  role: string;
  name: string;
  department: string;
}

/** The destinations, in the order the dialog has always shown them. A name only: the familiar departments need no description. */
export const HANDOFF_DESTINATIONS: readonly HandoffDestination[] = Object.freeze([
  { role: 'cad', name: 'CAD / Permit Team', department: 'cad' },
  { role: 'pm', name: 'Project Management', department: 'project-management' },
  { role: 'superintendent', name: 'Superintendent', department: 'project-management' },
  { role: 'warranty', name: 'Warranty', department: 'warranty' },
  { role: 'sales', name: 'Sales', department: 'sales' },
  { role: 'accounting', name: 'Accounting', department: 'accounting' },
]);

/** Which Cognito groups belong to each destination (moved unchanged from the dialog). */
export const HANDOFF_ROLE_GROUPS: Readonly<Record<string, readonly string[]>> = Object.freeze({
  cad: ['designer', 'admin', 'owner'],
  pm: ['pm', 'admin', 'owner'],
  superintendent: ['superintendent', 'admin', 'owner'],
  warranty: ['warranty', 'admin', 'owner'],
  sales: ['designer', 'admin', 'owner'],
  accounting: ['accounting', 'admin', 'owner'],
});

export interface HandoffUser {
  username: string;
  name: string;
  email: string;
  groups: string[];
}

/** The people who can receive a handoff to this destination: those in one of its groups. None before a destination is chosen. */
export function availableUsers(users: readonly HandoffUser[], role: string): HandoffUser[] {
  if (role === '') return [];
  const valid = HANDOFF_ROLE_GROUPS[role] ?? [];
  return users.filter((u) => Array.isArray(u.groups) && u.groups.some((g) => valid.includes(g)));
}

export type HandoffPhase = 'editing' | 'sending' | 'failed';

export interface HandoffState {
  role: string;
  userId: string;
  nextAction: string;
  notes: string;
  phase: HandoffPhase;
  error: string | null;
}

export function initialHandoff(): HandoffState {
  return { role: '', userId: '', nextAction: '', notes: '', phase: 'editing', error: null };
}

function editable(state: HandoffState): boolean {
  return state.phase !== 'sending';
}

/** Choosing another destination clears the person (they belonged to the previous destination). */
export function selectDestination(state: HandoffState, role: string): HandoffState {
  if (!editable(state) || typeof role !== 'string' || state.role === role) return state;
  if (role !== '' && !HANDOFF_DESTINATIONS.some((d) => d.role === role)) return state;
  return { ...state, role, userId: '', error: null };
}

export function selectPerson(state: HandoffState, userId: string): HandoffState {
  return editable(state) && typeof userId === 'string' ? { ...state, userId } : state;
}

export function setNextAction(state: HandoffState, value: string): HandoffState {
  return editable(state) && typeof value === 'string' ? { ...state, nextAction: value } : state;
}

export function setNotes(state: HandoffState, value: string): HandoffState {
  return editable(state) && typeof value === 'string' ? { ...state, notes: value } : state;
}

/** The submit is possible once a destination and a person are chosen, and not while a request runs. */
export function canSubmitHandoff(state: HandoffState): boolean {
  return state.phase !== 'sending' && state.role !== '' && state.userId !== '';
}

/** The request exactly as the dialog has always built it. The next action defaults; empty notes are left out. */
export function buildHandoffRequest(state: HandoffState, item: { title: string }, users: readonly HandoffUser[]): HandoffRequest {
  const user = users.find((u) => u.username === state.userId);
  const target = HANDOFF_DESTINATIONS.find((d) => d.role === state.role);
  const notes = state.notes.trim();
  return {
    targetRole: state.role,
    targetName: user?.name || state.userId,
    targetUserId: user?.username,
    targetDepartment: target?.department,
    title: `${item.title}`,
    nextAction: state.nextAction.trim() || HANDOFF_DEFAULT_NEXT_ACTION,
    notes: notes === '' ? undefined : notes,
  };
}

/** The submit click: `request` is non-null exactly when it must now be sent. A second click while it runs, or an incomplete choice, sends nothing. */
export function attemptHandoff(state: HandoffState, item: { title: string }, users: readonly HandoffUser[]): { state: HandoffState; request: HandoffRequest | null } {
  if (!canSubmitHandoff(state)) return { state, request: null };
  return { state: { ...state, phase: 'sending', error: null }, request: buildHandoffRequest(state, item, users) };
}

/** The server refused or did not answer: the choices stay, the message is the server's (or "Handoff failed"), and the person can try again. */
export function handoffFailed(state: HandoffState, thrown: unknown): HandoffState {
  if (state.phase !== 'sending') return state;
  if (thrown !== null && typeof thrown === 'object' && (thrown as { kind?: unknown }).kind === 'stale') return state;
  const message = thrown !== null && typeof thrown === 'object' && typeof (thrown as { message?: unknown }).message === 'string' ? (thrown as { message: string }).message.trim() : '';
  return { ...state, phase: 'failed', error: message === '' ? HANDOFF_FAILED_TEXT : message };
}

export interface HandoffView {
  consequenceLine: typeof HANDOFF_CONSEQUENCE_LINE;
  /** The destinations as a compact list (a dropdown, or two columns): a name each. */
  destinations: { role: string; name: string; selected: boolean }[];
  /** What the person picker shows: nothing before a destination, a loading line, "no users", or the people. */
  people: { kind: 'none' } | { kind: 'loading'; text: string } | { kind: 'empty'; text: string } | { kind: 'list'; options: { value: string; label: string; title: string }[]; selected: string };
  nextAction: string;
  notes: string;
  submit: { disabled: boolean; label: typeof HANDOFF_CONFIRM_LABEL | typeof HANDOFF_SENDING_LABEL };
  error: string | null;
}

/** The options of the person list: the short name (with what tells two alike apart), the stored value, and the full name as the option's title. */
function personOptions(available: readonly HandoffUser[]): { value: string; label: string; title: string }[] {
  const choices = disambiguatePeople(available.map((u) => ({ employeeId: u.username, displayName: u.name || u.email, email: u.email })));
  return available.map((u, index) => ({
    value: u.username,
    label: choices[index].detail !== '' ? `${choices[index].short} (${choices[index].detail})` : choices[index].short,
    title: choices[index].accessibleName,
  }));
}

export function describeHandoff(state: HandoffState, users: readonly HandoffUser[], loadingUsers: boolean): HandoffView {
  const available = availableUsers(users, state.role);
  const people: HandoffView['people'] =
    state.role === ''
      ? { kind: 'none' }
      : loadingUsers
        ? { kind: 'loading', text: 'Loading team members...' }
        : available.length === 0
          ? { kind: 'empty', text: 'No users found for this department.' }
          : { kind: 'list', options: personOptions(available), selected: state.userId };
  return {
    consequenceLine: HANDOFF_CONSEQUENCE_LINE,
    destinations: HANDOFF_DESTINATIONS.map((d) => ({ role: d.role, name: d.name, selected: d.role === state.role })),
    people,
    nextAction: state.nextAction,
    notes: state.notes,
    submit: { disabled: !canSubmitHandoff(state), label: state.phase === 'sending' ? HANDOFF_SENDING_LABEL : HANDOFF_CONFIRM_LABEL },
    error: state.phase === 'failed' ? state.error : null,
  };
}
